import { LitElement, html, property } from 'lit-element'
import { contextualize } from '@polleverywhere/cutlass'
import { register } from '@polleverywhere/hmr-custom-element'
import { bemBlock } from '@polleverywhere/bem-helpers'
import { defineElementSlot } from '@polleverywhere/element-helpers'
import './modal.scss'

/**
 * Renders a modal that completely blocks out content and prevents users from
 * interacting with anything behind it. Document focus is immediately moved into
 * the modal content area and keeps focus trapped within this context. By
 * default, modals can be closed either by hitting the `Escape` key or clicking
 * anywhere on the modal itself.
 *
 * This is a low-level construct that is not meant to be consumed directly by
 * application-level consumers. It is instead to be consumed by other components
 * that require a blocking modal, e.g. `pe-dialog`, `pe-action-sheet`.
 *
 * The word "modal" is used to describe a UI pattern that interrupts users and
 * demands an action by blocking out and preventing users from interacting with
 * anything outside the modal. Consider the contrast between "modal" vs
 * "modeless" (or "nonmodal"):
 * https://www.nngroup.com/articles/modal-nonmodal-dialog/
 *
 * ```
 * <pe-modal>
 *  <div>Display some content over the modal</div>
 * <pe-modal>
 * ```
 *
 * @element pe-modal
 *
 * @slot - Contains the content to overlay on top of the modal.
 *
 * @prop {boolean} open - Opens the modal.
 *
 * @fires {Object} closing - Fires when modal begins to close. Consumers may
 *  cancel the closing action by calling `preventDefault()` on the event. Doing
 *  so cancels the closing action and returns the modal to an open state.
 * @fires {Object} closed - Fires after modal is officially closed.
 * @fires {Object} opening - Fires when modal begins to open. Similar to the
 *  `closing` event, consumers may call `preventDefault()` to cancel the opening
 *  action and return the modal to a closed state.
 * @fires {Object} opened - Fires after modal is officially opened.
 *
 * @cssprop [--pe-modal-close-duration=75ms] - Closing transition duration.
 * @cssprop [--pe-modal-open-duration=150ms] - Opening transition duration.
 */
export default class Modal extends LitElement {
  static get tagName () { return 'pe-modal' }

  @property({ type: Boolean, reflect: true })
  open = false

  constructor () {
    super()

    // contextualize cutlass events
    contextualize(this, this.tagName)
  }

  get open () {
    return this.__open
  }

  set open (value) {
    if (value === this.__open) return

    // Always reset the cached action event when changing open state.
    const actionEvent = this._actionEvent
    delete this._actionEvent

    // Broadcast our intent to open/close
    let eventType = value ? 'opening' : 'closing'
    const detail = getEventDetail(actionEvent, value)
    const transitionCancelled = !this.dispatchEvent(new CustomEvent(eventType, {
      bubbles: true,
      cancelable: true,
      detail
    }))

    // Bail if anyone called preventDefault() on our '*ing' event
    if (transitionCancelled) return

    // If we're still running, actually open/close
    const oldValue = this.__open
    this.__open = value
    this.requestUpdate('open', oldValue)

    // Toggle listening to key events when open or close.
    toggleKeyListeners.call(this, value)

    eventType = value ? 'opened' : 'closed'
    this.dispatchEvent(new CustomEvent(eventType, {
      bubbles: true,
      detail
    }))

    // Add modifier to body so we can disable scrolling when modal is open.
    const openModifier = bem({ open: true }).split(' ')[1]
    document.body.classList.toggle(openModifier, value)

    // Always reset focus to modal on open after update completes.
    this.updateComplete.then(() => {
      this.open && resetFocus.call(this)
    })
  }

  connectedCallback () {
    super.connectedCallback()
    defineElementSlot(this)
  }

  createRenderRoot () { return this }

  render () {
    return html`
      <div @click=${handleCloseAction} class=${bem('backdrop')}></div>
      <div class=${bem('focus-boundary')} tabindex=0></div>
      <div class=${bem('content')}>${this.slots()}</div>
      <div class=${bem('focus-boundary')} tabindex=0></div>
    `
  }
}

function getEventDetail (actionEvent, opening) {
  return opening
    ? {}
    : {
        closeByClick: actionEvent?.type === 'click',
        closeByEscape: actionEvent?.key === 'Escape'
      }
}

function resetFocus (toEnd = false) {
  const selector = `.pe-modal__focus-boundary${toEnd ? ':last-child' : ''}`
  this.querySelector(selector).focus()
}

function toggleKeyListeners (enable) {
  if (!enable) {
    document.removeEventListener('keydown', this._handleKeydown, true)
    this._handleKeydown = undefined
    return
  }

  // If event was already registered, do nothing more.
  if (this._handleKeydown) return

  this._handleKeydown = (e) => {
    switch (e.key) {
      case 'Escape':
        handleCloseAction.call(this, e)
        break

      case 'Tab': {
        // When tabbing away from one of our focus boundaries, reset focus to
        // the correct focus boundary (before or after) so that the tab press
        // sends sequential focus to the correct element.
        const activeElement = document.activeElement
        if (activeElement.classList.contains('pe-modal__focus-boundary')) {
          resetFocus.call(this, e.shiftKey)
        }
        break
      }
    }
  }
  document.addEventListener('keydown', this._handleKeydown, true)
}

function handleCloseAction (e) {
  this._actionEvent = e
  this.open = false
}

const bem = bemBlock(Modal.tagName)
register(module, Modal.tagName, Modal)
